import { handleApi } from "../utils/error";
import { BaseCache, makeDefectListCache, makeDeviceCache, makeEffectiveTrailerDeviceCache, makeUserCache } from "../utils/storage/cache";
import { TrailerAttachments } from "../model/TrailerAttachments";
import { dispatcher, DispatcherEvents } from "../utils/dispatcher";
import * as logger from "../utils/logger";
import DVIRLog, { IDefectRemark, IDvirDefects, IDVIRLog, TLogType } from "../model/DVIRLog";
import { DVIRLogsService } from "../model/DVIRLogsService";
import Notifications, { DVIR_UNSYNCED_INSPECTION_STRING } from "../utils/notifications";
import * as common from "./common";
import { getCurrentVehicle } from "../utils/device";
import { CancelablePromise as Promise } from "../utils/cancelablePromise";
import { TrailerIdentityReconciler } from "./trailerIdentityReconciler";
import { extend, hashToArray, isEqual, jsonClone } from "../utils/vanilla";
import { getDefectListsForAssetType } from "../utils/defectList";
import { syncDefectLists } from "./misc";
import { getServerDate } from "../utils/storage/date";
import BaseSync, { ISyncAddSet, ISyncOptions } from "./base";
import { FeatureFlagName } from "../utils/featureFlagName";
import { isUnsyncedDvirLog } from "../store/dvirLogs/reducers";
import toastr from "../utils/toastr";
import { DataActions } from "../store/data/actions";
import { getImage, isMediaForDvirLog, isMediaPendingDelivery } from "../model/Image";
import { NOTRAILERID } from "../model/DutyStatusLog";
import { IImages } from "../store/data/types";
import { Trailer } from "../model/Trailer";
import { IModifiedType } from "./sync.types";
import { optionalDateTimeComparator } from "../utils/date";
import { IApiError, TMultiCallParam } from "../utils/api.interfaces";
import { ITrailerAttachment } from "../model/TrailerAttachment";
import { DvirLogActions } from "../store/dvirLogs/actions";
import { FeedTypeKey } from "./common";
import { DeviceDefectListActions, TrailerDefectListActions } from "../store/defectLists/actions";
import { IDefect, TDefectAssetType } from "../model/Defect";
import { Device } from "../model/Device";
import { isArgumentException, isDuplicateException, isInvalidApiOperationException,
    isInvalidOperationException, isInvalidPermissionsException, isOverLimitException, isRelationViolatedException } from "../utils/errorHandler/error.types";
import * as errorMessages from "../utils/errorHandler/error.messages";
import { canSkipCertify } from "../ui/dvir/helper";
import { MeasureName } from "../utils/performanceProfiler/measureName";
import PerformanceProfileBatcher from "../utils/performanceProfiler/performanceProfileBatcher";
import {
    fromTrailerDvirLogWire, isTrailerDvirRef, ITrailerAttachmentSearch, normalizeTrailerAttachmentResults,
    primeTrailerDeviceIdentity, toDvirAssetSearch, toDvirLogWireEntity, toTrailerAttachmentGetCall, usesTrailerDeviceIdentity
} from "./trailerAttachmentDeviceLink";

    type TTrailerDvirReclassifier = (log: IDVIRLog, expectedTrailerId?: string) => IDVIRLog;

    const MIN_VERSION = "0000000000000000";
    const REPAIR_STATUS_PRIORITY: Record<string, number> = { "NotRepaired": 0, "NotNecessary": 1, "Repaired": 2 };
    interface IDvirSearchParams {
        typeName: "DVIRLog";
        search: IDvirLogSearch;
        resultsLimit?: number;
        fromVersion?: string | null;
    }

    interface IDvirLogSearch {
        deviceSearch?: IIdEntity;
        trailerSearch?: IIdEntity;
        fromDate?: string;
        toDate?: string;
        includeBoundaryLogs?: boolean;
        logTypes?: TLogType[];
    }

    /** Attempts a log gets per session before it is treated as undeliverable. */
    const DVIR_SYNC_RETRY_BUDGET = 5;
    /** Retained undeliverable logs beyond this count indicate a runaway condition worth reporting. */
    const MAX_RETAINED_UNSYNCED_DVIR_LOGS = 20;

    /**
     * An Add that is out of retries: retained locally with no copy on the server.
     *
     * Deliberately narrower than isUnsyncedDvirLog, which also matches "Set". A Set has a server
     * copy and keeps its per-attempt charging, so a spent Set is not stranded and must not count
     * towards the retained cap or raise the driver notification.
     */
    const isTerminalUnsyncedAdd = (log?: IDVIRLog): boolean => {
        return !!log && log.modified === "Add" && (log._syncCounter ?? DVIR_SYNC_RETRY_BUDGET) <= 0;
    };

    export default class DvirSync extends BaseSync {
        private vehicleFeedVersion: string | undefined;
        private trailerFeedVersion: IIdDictionary<string> = {};
        private firstSyncComplete = false;
        /** Ids already reported as retained by prune(), so a stuck log is logged once per session, not every sync cycle. */
        private readonly retainedUnsyncedLogIdsLogged = new Set<string>();
        /** Ids whose retry budget has already been charged in the current fetchData cycle. */
        private readonly syncCounterDecrementedThisCycle = new Set<string>();
        /** Ids already reported as having reached the terminal undeliverable state this session. */
        private readonly terminalUnsyncedLogIdsReported = new Set<string>();

        // A log whose push keeps failing reaches the undelivered-media branch on every cycle. Reported
        // once per log per session, matching terminalUnsyncedLogIdsReported — the condition is
        // permanent for that log, so repeating it adds nothing and buries the first occurrence.
        private readonly undeliveredMediaLogIdsReported = new Set<string>();
        private readonly deviceCache: BaseCache<IDevice | Device>;
        private readonly userCache: BaseCache<IUser>;
        private readonly defectListCache: BaseCache<IDefect>;
        private readonly trailerIdentityReconciler: TrailerIdentityReconciler;

        constructor (options: ISyncOptions, private readonly dvirLogsService: DVIRLogsService) {
            super(options);
            // MYG-148762: make the store the source of truth for the trailer-identity flag used by the
            // storeless wire helpers in ./trailerAttachmentDeviceLink.
            primeTrailerDeviceIdentity(this.store);
            this.defectListCache = makeDefectListCache(this.store);
            this.deviceCache = makeDeviceCache(this.store);
            this.userCache = makeUserCache(this.store);
            this.trailerIdentityReconciler = new TrailerIdentityReconciler(this.api, this.store);
        }

        defaults() {
            return {
                name: "Sync: DVIR"
            };
        }

        getLogs(): IIdDictionary<DVIRLog> {
            return this.dvirLogsService.getDvirLogsAsDictionary(this.api.getUser()?.id);
        }

        setLogs (logsToSave: IIdDictionary<DVIRLog>) {
            this.updateAllMediaIds(logsToSave);
            let apiUser = this.api.getUser();
            apiUser?.id && this.store.dispatch(DvirLogActions.setDvirLogs({userId: apiUser.id, logs: logsToSave}));
        }

        clear() {
            this.setLogs({});
        }

        updateAllMediaIds(logsToSave: IIdDictionary<DVIRLog>) {
            let imagesClone = extend(true, {}, this.store.getState().data.images) as IImages;
            Object.keys(logsToSave).forEach(key => {
                let dvirDefects = logsToSave[key].dVIRDefects;
                if (dvirDefects && dvirDefects.length) {
                    for (let i = 0; i < dvirDefects.length; i++) {
                        let dvirDefect = dvirDefects[i];
                        let defectRemarks = dvirDefect.defectRemarks;
                        if (defectRemarks && defectRemarks.length) {
                            for (let j = 0; j < defectRemarks.length; j++) {
                                let mediaFiles = defectRemarks[j].mediaFiles;
                                if (mediaFiles && mediaFiles.length) {
                                    for (let k = 0; k < mediaFiles.length; k++) {
                                        let mediaFile = mediaFiles[k];
                                        if (!imagesClone[mediaFile.id]) {
                                            imagesClone[mediaFile.id] = getImage("", true, dvirDefect.defect.id, false, true);
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                this.store.dispatch(DataActions.setImages(imagesClone));
            });
        }

        prune(logs: IIdDictionary<DVIRLog>) {
            let trailerAttachmentCollection = new TrailerAttachments(this.api, this.store);

            let vehicle = getCurrentVehicle() || null;
            let trailers: Trailer[] = trailerAttachmentCollection.getCurrentTrailers();
            let logCollection = hashToArray(logs).map(log => new DVIRLog(log));
            let prunedLogs: IIdDictionary<DVIRLog> = {};

            let vehicleId = vehicle && vehicle.id !== "NoDeviceId" ? vehicle.id : undefined;
            let vehicleLog = vehicleId ? this.dvirLogsService.getLatestRequiredLog(logCollection, vehicleId) : undefined;
            let additionalLogs: DVIRLog[] = vehicleId ? this.dvirLogsService.getDvirsAfterDate(logCollection, vehicleId, vehicleLog?.dateTime) : [];
            let logsToKeep: DVIRLog[] = vehicleLog ? [vehicleLog].concat(additionalLogs) : additionalLogs;

            trailers.forEach(trailer => {
                let trailerId = trailer.id;
                let trailerLog = this.dvirLogsService.getLatestRequiredLog(logCollection, trailerId);
                additionalLogs = this.dvirLogsService.getDvirsAfterDate(logCollection, trailerId, trailerLog?.dateTime);
                logsToKeep = logsToKeep.concat(trailerLog ? [trailerLog].concat(additionalLogs) : additionalLogs);
            });

            logsToKeep.map(log => prunedLogs[log.id] = log.toJSON());

            // Captured before retention: these are the logs that belong to the current asset
            // selection. Callers that act on "what the driver is using right now" - the critical
            // defect notification - must use this, not the persisted set, which also carries
            // retained logs for assets the driver no longer has selected.
            const assetRelevantLogIds = Object.keys(prunedLogs);

            this.retainUnsyncedLogs(logCollection, prunedLogs);
            return { prunedLogs, assetRelevantLogIds };
        }

        mergeDvirLogs (serverLogs: DVIRLog[], localLogs: IIdDictionary<DVIRLog>) {
            let currentUser: IUser | null = this.api.getUser();
            let localLogsArray = hashToArray(localLogs);

            //Merge in the server logs
            serverLogs.forEach((log) => {
                let server: DVIRLog = new DVIRLog(log);
                let local: DVIRLog = localLogs[log.id];
                let previousState: DVIRLog;
                let modifiedState: IModifiedType | undefined;

                //Server log exists locally
                if (local) {
                    local = new DVIRLog(local);
                    previousState = new DVIRLog(local.previousState);
                    modifiedState = local.modified;

                    /*
                    Four scenarios:
                    local changed, server same => take local
                    local changed, server changed => conflict if local was "edit", merge local and server if local was "add"
                    local same, server changed => take server
                    local same, server same => do nothing
                    */

                    //Local changed
                    if (modifiedState) {
                        //Server changed
                        if (previousState.version !== server.version) {
                            if (modifiedState === "Add") {
                                // Server has a different version, merge them
                                logger.log(logger.Source.SYNC, "Merged local DVIR log with server", currentUser);
                                localLogs[log.id] = common.merge(localLogs[log.id], server.toJSON(), {
                                    modified: "Set"
                                });
                            } else {
                                logger.error(logger.Source.SYNC, `DVIR conflict (both server and client changed). Local ID: ${ local.id }, Server ID: ${ server.id }`, currentUser);
                            }
                        }
                        //Local same
                    } else {
                        // Server changed (take server version)
                        if (local.version !== server.version) {
                            logger.log(logger.Source.SYNC, "Updated local DVIR log from server", currentUser);
                            localLogs[log.id] = server.toJSON();

                            let address = local._address;
                            let isManual = local._isManualAddress;
                            if (address && isManual) {
                                localLogs[log.id]._address = address;
                                localLogs[log.id]._isManualAddress = isManual;
                            }

                            if (local.id === server.id) {
                                let tempLocal = jsonClone<IDvirDefects[]>(local.dVIRDefects);
                                let tempServer = jsonClone<IDvirDefects[]>(server.dVIRDefects);

                                if (tempLocal && tempLocal.length !== 0) {
                                    tempLocal.forEach(defect => {
                                        delete defect.repairDateTime;
                                    });
                                }

                                if (tempServer && tempServer.length !== 0) {
                                    tempServer.forEach(defect => {
                                        delete defect.repairDateTime;
                                    });
                                }

                                let localCert = local.certifiedBy;
                                let serverCert = server.certifiedBy;

                                if (!isEqual(tempLocal, tempServer) || (serverCert && (localCert as IIdEntity).id !== (serverCert as IIdEntity).id)) {
                                    dispatcher.trigger(DispatcherEvents.DvirLogSet, log.id);
                                }
                            }
                        }
                    }
                } else {
                    localLogs[log.id] = log;
                }

                if (server.dVIRDefects?.length && server.dVIRDefects.some(defect => defect.repairStatus !== "NotRepaired")) {
                    this.updateOutdatedDefectNew(
                        server,
                        localLogsArray.filter(localLog => localLog.id !== server.id
                            && localLog.dVIRDefects?.some(localDefect => server.dVIRDefects?.some(serverDefect => serverDefect.id === localDefect.id)))
                    );
                }
            });

            return localLogs;
        }

        hasUnsyncedCertifyLogError(errorObject: IApiError, localLogs: IIdDictionary<DVIRLog>, invalidLog: DVIRLog) {
            return errorMessages.isMissingCertifiedDvir(errorObject) && this.hasUnsyncedCertifiedDvir(localLogs, invalidLog);
        }

        getCombinedRemarks(combinedDefectRemarks: IDefectRemark[]) {
            const remarkDateTimes = new Set();
            return combinedDefectRemarks.filter(item => {
                if (remarkDateTimes.has(item.dateTime)) {
                    return false;
                } else {
                    remarkDateTimes.add(item.dateTime);
                    return true;
                }
            });
        }

        updateOutdatedDefectNew(logFromServer: DVIRLog, logsFromLocal: DVIRLog[]) {
            logsFromLocal.sort(optionalDateTimeComparator());
            let updatedDefect = false;
            if (logsFromLocal.length) {
                (logFromServer.dVIRDefects || []).forEach(serverDefect => {
                    if (serverDefect.repairStatus === "NotRepaired") {
                        return;
                    }

                    let carryOverDefect: IDvirDefects | null = null;
                    logsFromLocal.forEach(log => {
                        let localDefect = (log.dVIRDefects || []).find(defect => defect.id === serverDefect.id);
                        if (localDefect) {
                            let repairRemarks = this.getCombinedRemarks((carryOverDefect?.defectRemarks || serverDefect.defectRemarks || []).concat(localDefect.defectRemarks || []));
                            carryOverDefect = extend(localDefect, carryOverDefect || serverDefect, { defectRemarks: repairRemarks });
                            if (serverDefect.repairStatus === "Repaired" && log.dateTime && serverDefect.repairDateTime && log.dateTime > serverDefect.repairDateTime && log.modified === "Add") {
                                log.dVIRDefects = log.dVIRDefects?.filter(defect => defect.id !== serverDefect.id);
                            }
                            updatedDefect = true;
                        }
                    });
                });
            }
            return updatedDefect;
        }

        getDvirBeforeLog(invalidLog: DVIRLog) {
            return new Promise((resolve, reject) => {
                if (invalidLog.dVIRDefects?.length && invalidLog.isActive()) {
                    let dvirLogSearch: IDvirLogSearch = {
                        fromDate: invalidLog.dateTime,
                        toDate: invalidLog.dateTime,
                        includeBoundaryLogs: true
                    };

                    extend(dvirLogSearch, toDvirAssetSearch(invalidLog));

                    // Get the last DVIR for this asset, it should contain the defect in question
                    this.api.call("Get", {
                        typeName: "DVIRLog",
                        search: dvirLogSearch,
                        resultsLimit: 1
                    }, logs => {
                        const expectedTrailerId = invalidLog.getResourceType() === "trailer" ? invalidLog.getResource().id : undefined;
                        const reclassify = this.makeTrailerDvirReclassifier();
                        resolve((logs || []).map(log => reclassify(log, expectedTrailerId)));
                    }, reject);
                } else {
                    resolve([]);
                }
            });
        }

        getMissingDefectFromServer(localLogs: IIdDictionary<DVIRLog>, invalidLog: DVIRLog) {
            return new Promise((resolve, reject) => {
                let updatedDefect = false;
                return this.getDvirBeforeLog(invalidLog)
                    .then((logs): Promise<void> | void => {
                        if (logs.length) {
                            let logFromServer = new DVIRLog(logs[0]);
                            if (logFromServer.dVIRDefects?.length) {
                                const localLog = localLogs[invalidLog.id];

                                // Path 1: defects completely absent from the local log (no matching template defect.id).
                                // Append the server defect directly regardless of repair status.
                                const trulyMissingDefects = logFromServer.dVIRDefects.filter(serverDefect =>
                                    (serverDefect.repairStatus === "NotRepaired" || serverDefect.repairStatus === "NotNecessary")
                                    && !localLog?.dVIRDefects?.some(localDefect => localDefect.defect.id === serverDefect.defect.id));

                                if (trulyMissingDefects.length > 0) {
                                    // Path 1: defects completely absent — append directly and stop.
                                    // If this still causes an exception on retry, it will re-enter path 2 then.
                                    trulyMissingDefects.forEach(serverDefect => {
                                        if (localLog?.dVIRDefects) {
                                            localLog.dVIRDefects.push(serverDefect);
                                            updatedDefect = true;
                                        }
                                    });
                                    if (updatedDefect) {
                                        // Server defects may only have defect.id — hydrate severity so
                                        // inspect/view don't operate on a partially populated defect object.
                                        const assetType: TDefectAssetType = this.isTrailerDvir(invalidLog) ? "Trailer" : "Device";
                                        return this.populateDefectSeverity([localLog], assetType)
                                            .then(() => resolve(updatedDefect));
                                    }
                                } else {
                                    // Path 2: defects present locally by template (defect.id) but with a mismatched DVIRDefect id.
                                    // Takes the server id, combines remarks, and picks the more advanced repair state
                                    // (NotRepaired < NotNecessary < Repaired) so a local repair is never silently lost.
                                    const idMismatchDefects = logFromServer.dVIRDefects.filter(serverDefect =>
                                        (serverDefect.repairStatus === "NotRepaired" || serverDefect.repairStatus === "NotNecessary")
                                        && localLog?.dVIRDefects?.some(localDefect => localDefect.defect.id === serverDefect.defect.id
                                            && localDefect.id !== serverDefect.id));

                                    idMismatchDefects.forEach(serverDefect => {
                                        const localDefect = localLog?.dVIRDefects?.find(d => d.defect.id === serverDefect.defect.id);
                                        if (!localDefect) {
                                            return;
                                        }
                                        const combinedRemarks = this.getCombinedRemarks((serverDefect.defectRemarks || []).concat(localDefect.defectRemarks || []));
                                        const localPriority = REPAIR_STATUS_PRIORITY[localDefect.repairStatus || "NotRepaired"];
                                        const serverPriority = REPAIR_STATUS_PRIORITY[serverDefect.repairStatus || "NotRepaired"];
                                        const repairSource = localPriority > serverPriority ? localDefect : serverDefect;
                                        extend(localDefect, {
                                            id: serverDefect.id,
                                            repairStatus: repairSource.repairStatus,
                                            repairUser: repairSource.repairUser,
                                            repairDateTime: repairSource.repairDateTime,
                                            defectRemarks: combinedRemarks
                                        });
                                        updatedDefect = true;
                                    });
                                }
                            }
                        }
                        resolve(updatedDefect);
                    })
                    .catch(reject);
                });
        }

        updateOutdatedDefectFromServerNew(localLogs: IIdDictionary<DVIRLog>, invalidLog: DVIRLog) {
            return new Promise((resolve, reject) => {
                let updatedDefect = false;
                return this.getDvirBeforeLog(invalidLog)
                    .then(logs => {
                        if (logs.length) {
                            let logFromServer = new DVIRLog(logs[0]);
                            let logsFromLocal = hashToArray(localLogs)
                                .filter(localLog => localLog.dVIRDefects?.some(localDefect => logFromServer.dVIRDefects?.some(serverDefect => serverDefect.id === localDefect.id)));
                            if (logFromServer?.dVIRDefects?.length && logsFromLocal.length) {
                                updatedDefect = this.updateOutdatedDefectNew(logFromServer, logsFromLocal);
                            }
                        }
                        resolve(updatedDefect);
                    }, reject);
            });
        }

        isDeviceDvir(log: DVIRLog): boolean {
            return !this.isTrailerDvir(log) && log.device && log.device.id !== "NoDeviceId";
        }

        isTrailerDvir(log: DVIRLog): boolean {
            // Same constant isMediaForDvirLog matches on, so the two cannot drift.
            return log.trailer && log.trailer.id !== NOTRAILERID;
        }

        removeDeletedLogsFromServer(localLogs: IIdDictionary<DVIRLog>): Promise<void> {
            const unmodifiedIds = Object.keys(localLogs).filter(id => !localLogs[id].modified);

            if (!unmodifiedIds.length) {
                return Promise.resolve();
            }

            const calls: TMultiCallParam[] = unmodifiedIds.map(id => ["Get", {
                typeName: "DVIRLog",
                search: { id },
                resultsLimit: 1
            }]);

            return this.api.multiCallPromise(calls).then((results: IDVIRLog[][]) => {
                results.forEach((log, i) => {
                    if (!log.length) {
                        delete localLogs[unmodifiedIds[i]];
                    }
                });
            }).catch(() => {
                logger.error(logger.Source.SYNC, "Failed to verify DVIR log existence on server", this.api.getUser());
            });
        }

        pushChangedToServer(localLogs: IIdDictionary<DVIRLog>) {
            return new Promise<IIdDictionary<DVIRLog>>((resolve, reject) => {
                let currentUser: IUser | null = this.api.getUser();
                let calls: [IModifiedType, ISyncAddSet<IDVIRLog>][] = [];
                let activeDrivers: any = {};

                if (!currentUser) {
                    resolve(localLogs);
                    return;
                }

                let images = extend(true, {}, this.store.getState().data.images) as IImages;

                // Prepare local logs for upload
                // Logs should be sorted by insert order, but just in case
                let sortedDvirs: DVIRLog[] = hashToArray(localLogs).sort(optionalDateTimeComparator());
                sortedDvirs.forEach((log) => {
                    if (log.id === null) {
                        throw new Error("Attempting to sync a record without an id");
                    }

                    // Find relevant users to lookup
                    if (log.driver !== "NoUserId") {
                        activeDrivers[log.driver.id] = log.driver;
                    }

                    if (log.repairedBy !== "NoUserId") {
                        activeDrivers[log.repairedBy.id] = log.repairedBy;
                    }

                    if (log.certifiedBy !== "NoUserId") {
                        activeDrivers[log.certifiedBy.id] = log.certifiedBy;
                    }

                    if (log.dVIRDefects && log.dVIRDefects.length > 0) {
                        log.dVIRDefects.forEach(dvirDefect => {
                            if (dvirDefect.repairUser && dvirDefect.repairUser.id !== "NoUserId") {
                                activeDrivers[dvirDefect.repairUser.id] = dvirDefect.repairUser;
                            }
                        });
                    }

                    if (log.modified) {
                        let sanitizedLog: IDVIRLog = extend({}, log);
                        this.removeClientSideProperties(sanitizedLog);

                        // Converting device to "NoDeviceId" instead of Device/IIdEntity type as required by the API
                        if (sanitizedLog.device?.id === "NoDeviceId") {
                            sanitizedLog.device = undefined;
                        }

                        // Converting Trailer to "NoTrailerId" instead of Trailer/IIdEntity type as required by the API
                        if (sanitizedLog.trailer?.id === "NoTrailerId") {
                            sanitizedLog.trailer = undefined;
                        }

                        if (sanitizedLog.driver === "NoUserId") {
                            sanitizedLog.driver = undefined;
                        }

                        if (sanitizedLog.repairedBy === "NoUserId") {
                            sanitizedLog.repairedBy = undefined;
                        }

                        if (sanitizedLog.certifiedBy === "NoUserId") {
                            sanitizedLog.certifiedBy = undefined;
                        }

                        if (log.modified === "Add" || log.modified === "Set") {
                            if (log.dVIRDefects && log.dVIRDefects.length > 0) {
                                const logImageIds = Object.keys(images).filter(key => isMediaForDvirLog(images[key], log));
                                const logImages: IImages = {};
                                logImageIds.forEach(key => {
                                    logImages[key] = images[key];
                                });

                                for (let i = 0; i < log.dVIRDefects.length; i++) {
                                    let defect: IDvirDefects = log.dVIRDefects[i];
                                    this.updateRepairRemarkWithMediaFile(defect, logImages, log.dateTime || "", log.modified);
                                }

                                // This push is the log's only chance to carry its photos: a
                                // successful Add clears `modified`, and the block above never runs
                                // for it again. A photo whose bytes are still missing is therefore
                                // lost to this inspection for good. sync.ts uploads pending media
                                // ahead of DVIR precisely to prevent this, so reaching here means
                                // that upload failed too — rare, but silent until now, and silence
                                // is what made ESUPSD-52472 impossible to confirm after the fact.
                                const undelivered = logImageIds.filter(key => isMediaPendingDelivery(images[key]));
                                if (undelivered.length > 0 && !this.undeliveredMediaLogIdsReported.has(log.id)) {
                                    this.undeliveredMediaLogIdsReported.add(log.id);
                                    logger.error(
                                        logger.Source.SYNC,
                                        `DVIR log is being sent without media that never reached the server; it cannot be attached afterwards. DVIR ID: ${ log.id }. Media IDs: ${ undelivered.join(", ") }.`,
                                        currentUser
                                    );
                                }
                            }
                            if (log._syncCounter === undefined) {
                                log._syncCounter = 5;
                            }
                            if (log._syncCounter > 0) {
                                calls.push([log.modified, { typeName: "DVIRLog", entity: toDvirLogWireEntity(sanitizedLog) }]);
                            } else if (log.modified === "Add") {
                                // ESUPSD-53433: an unsynced Add is kept rather than deleted, so once
                                // it stops being pushed it stays on the device indefinitely with no
                                // copy on the server.
                                logger.error(
                                    logger.Source.SYNC,
                                    `DVIR log exhausted its sync retries and is retained locally without a server copy. DVIR ID: ${ log.id }.`,
                                    currentUser
                                );
                                this.reportTerminalUnsyncedLog(log, localLogs, currentUser);
                            }
                        }
                    }
                });

                this.store.dispatch(DataActions.setImages(images));

                // Add DVIR drivers/repairers/certifiers to user cache, if necessary
                void this.userCache.getOrFetch(this.api, hashToArray<IIdEntity>(activeDrivers).map(driver => ({ id: driver.id })), currentUser.id);

                if (calls.length > 0) {
                    const formattedCallsForLogger = common.formatCallForLogger("DVIR Logs", calls);
                    logger.log(logger.Source.SYNC, formattedCallsForLogger, currentUser);

                    this.api.multiCall(calls, (data) => {
                        let getLocalEntity = (entity: IDVIRLog) => localLogs[entity.id];
                        this.resolveSyncedEntities(calls, data, getLocalEntity);

                        if (data.length > 0) {
                            this.fetchLogs(localLogs).then(resolve, reject);
                        } else {
                            resolve(localLogs);
                        }
                    // eslint-disable-next-line complexity
                    }, (errorObject: IApiError) => {
                        if (isOverLimitException(errorObject)) {
                            reject(errorObject);
                            return;
                        }
                        let requestIndex = errorObject.data.requestIndex;
                        let call = calls[requestIndex];
                        let log = call && call[1].entity;
                        const logId = log && log.id;
                        const logIsInLocalStorage = logId && localLogs.hasOwnProperty(logId);
                        let localLog = logIsInLocalStorage && localLogs[logId];

                        if (localLog && localLog._syncCounter !== undefined && this.shouldChargeSyncRetry(localLog)) {
                            localLog._syncCounter--;
                        }

                        if (isDuplicateException(errorObject) && localLog) {
                            // This log is already in the database, make it a "Set" and sync again
                            localLog.modified = "Set";
                            this.resolveAddConflicts(localLogs)
                                .then(() => this.pushChangedToServer(localLogs))
                                .then(resolve, reject);
                        } else if (isRelationViolatedException(errorObject) && localLog) {
                            let fkConstraint = common.getForeignKeyConstraint(errorObject);
                            let errorObjectId = common.getForeignKeyErrorObjectId(errorObject);

                            if (fkConstraint) {
                                fkConstraint = fkConstraint.toLowerCase();
                                switch (fkConstraint) {
                                    case "fk_dvirlog_users":
                                        localLog.driver = "NoUserId";
                                        this.pushChangedToServer(localLogs).then(resolve, reject);
                                        break;
                                    case "fk_dvirlog_repairusers":
                                        localLog.repairedBy = "NoUserId";
                                        this.pushChangedToServer(localLogs).then(resolve, reject);
                                        break;
                                    case "fk_dvirdefect_users":
                                        if (errorObjectId) {
                                            this.deleteDefectRepairUser(localLog, errorObjectId);
                                        }
                                        this.pushChangedToServer(localLogs).then(resolve, reject);
                                        break;
                                    case "fk_defectremark_users":
                                        localLog.dVIRDefects?.forEach(value => {
                                            this.deleteRemarkUser(value.defectRemarks, errorObjectId);
                                        });
                                        this.pushChangedToServer(localLogs).then(resolve, reject);
                                        break;
                                    case "fk_dvirlog_certifyusers":
                                        localLog.certifiedBy = "NoUserId";
                                        this.pushChangedToServer(localLogs).then(resolve, reject);
                                        break;
                                    case "fk_dvirlog_trailer":
                                        delete localLogs[logId];
                                        this.pushChangedToServer(localLogs).then(resolve, reject);
                                        break;
                                    case "fk_dvirdefect_defect":
                                        delete localLogs[logId];
                                        this.pushChangedToServer(localLogs).then(resolve, reject);
                                        break;
                                    case "fk_dvirlog_vehicle":
                                        delete localLogs[logId];
                                        this.clearVehicleIfNecessary(new DVIRLog(log));
                                        this.pushChangedToServer(localLogs).then(resolve, reject);
                                        break;
                                    case "fk_dvirlog_defect":
                                        delete localLogs[logId];
                                        this.showDefectListToastrMessage(log);
                                        this.setLogs(localLogs);
                                        reject(errorObject);
                                        break;
                                    default:
                                        // A foreign key we are not handling. Need to error out.
                                        dispatcher.trigger(DispatcherEvents.SyncDvir);
                                        reject(errorObject);
                                        break;
                                }
                            } else {
                                // the same as not in the system
                                delete localLogs[logId];
                                this.pushChangedToServer(localLogs).then(resolve, reject);
                            }
                        } else if ((errorMessages.isNotInSystemException(errorObject) || errorMessages.isEarlierLogException(errorObject)) && logIsInLocalStorage) {
                            delete localLogs[logId];
                            this.pushChangedToServer(localLogs).then(resolve, reject);
                        } else if (this.hasUnsyncedCertifyLogError(errorObject, localLogs, new DVIRLog(log))) {
                            this.pushChangedToServer(localLogs).then(resolve, reject);
                        } else if (errorMessages.isMissingDefectsOnNewDvirException(errorObject)) {
                            this.getMissingDefectFromServer(localLogs, new DVIRLog(log))
                                .then(wasDefectUpdated => wasDefectUpdated ? Promise.resolve() : this.resetInvalidEdit(localLogs, log))
                                .then(() => this.pushChangedToServer(localLogs))
                                .then(resolve, reject);
                        } else if (this.isRepairStatusChangedException(errorObject)) {
                            this.updateOutdatedDefectFromServerNew(localLogs, new DVIRLog(log))
                                .then(wasDefectUpdated => wasDefectUpdated ? Promise.resolve() : this.resetInvalidEdit(localLogs, log))
                                .then(() => this.pushChangedToServer(localLogs))
                                .then(resolve, reject);
                        } else if (errorMessages.isMissingRepairInfoException(errorObject) && localLog) {
                            // ESUPSD-53433: a repaired defect reached the server with no repair info.
                            // Backfill it and retry rather than falling through to the generic branch,
                            // which discards the log - destroying an inspection that was never synced.
                            logger.error(
                                logger.Source.SYNC,
                                `DVIR missing defect repair info: ${ errorObject.message }. DVIR ID: ${ log.id }.`
                            );
                            const wasBackfilled = this.backfillMissingRepairInfo(localLog);
                            (wasBackfilled ? Promise.resolve() : this.resetInvalidEdit(localLogs, log))
                                .then(() => this.pushChangedToServer(localLogs))
                                .then(resolve, reject);
                        } else if (this.isInvalidLog(errorObject) && logIsInLocalStorage) {
                            // Handle certification conflicts and missing certified DVIR errors
                            // These are expected workflow issues - log for auditing but don't report to Sentry
                            const isCertifyConflict = errorMessages.isCertificationConflict(errorObject);
                            if (isCertifyConflict) {
                                logger.error(
                                    logger.Source.SYNC,
                                    `DVIR certification conflict: ${ errorObject.message }. DVIR ID: ${ log.id }.`
                                );
                            }

                            const isMissingCertifiedDvir = errorMessages.isMissingCertifiedDvir(errorObject);
                            if (isMissingCertifiedDvir) {
                                const dvirLog = new DVIRLog(localLogs[logId]);
                                const skipCertify = canSkipCertify(this.store, dvirLog._device);
                                logger.error(
                                    logger.Source.SYNC,
                                    `DVIR certification dependency error: ${ errorObject.message }. DVIR ID: ${ log.id }. device.id: ${ dvirLog.device?.id || "undefined" }. _device.id: ${ dvirLog._device?.id || "undefined" }. canSkipCertify: ${ skipCertify }`
                                );
                                this.removeErroneousDvirs(localLogs, dvirLog);
                            }

                            const isCannotChangeDefectRepair = errorMessages.isCannotChangeDefectRepairException(errorObject);
                            if (isCannotChangeDefectRepair) {
                                logger.error(
                                    logger.Source.SYNC,
                                    `DVIR defect repair change error: ${ errorObject.message }. DVIR ID: ${ log.id }.`
                                );
                            }

                            const isRepairDateTimeMismatch = errorMessages.isRepairDateTimeMismatch(errorObject);
                            if (isRepairDateTimeMismatch) {
                                logger.error(
                                    logger.Source.SYNC,
                                    `DVIR repair datetime mismatch: ${ errorObject.message }. DVIR ID: ${ log.id }.`
                                );
                            }

                            const isCertifiedByMismatch = errorMessages.isCertifiedByPersonMismatch(errorObject);
                            if (isCertifiedByMismatch) {
                                logger.error(
                                    logger.Source.SYNC,
                                    `DVIR certified-by person mismatch: ${ errorObject.message }. DVIR ID: ${ log.id }.`
                                );
                            }

                            const isUnrepairedDefects = errorMessages.hasUnrepairedRegulatedDefects(errorObject);
                            if (isUnrepairedDefects) {
                                logger.error(
                                    logger.Source.SYNC,
                                    `DVIR unrepaired regulated defects: ${ errorObject.message }. DVIR ID: ${ log.id }.`
                                );
                            }

                            if (!isCertifyConflict && !isMissingCertifiedDvir && !isCannotChangeDefectRepair && !isRepairDateTimeMismatch && !isCertifiedByMismatch && !isUnrepairedDefects) {
                                // Other invalid log errors - report to Sentry
                                handleApi(errorObject, true);
                            }

                            this.resetInvalidEdit(localLogs, log)
                                .then(() => this.pushChangedToServer(localLogs))
                                .then(resolve, reject);
                        } else {
                            dispatcher.trigger(DispatcherEvents.SyncDvir);
                            reject(errorObject);
                        }
                    });
                } else {
                    resolve(localLogs);
                }
            });
        }

        isRepairStatusChangedException(errorObject: IApiError) {
            return errorMessages.isRepairStatusChangedException(errorObject);
        }

        isInvalidLog(errorObject: IApiError) {
            return errorMessages.isDvirMustHaveResourceException(errorObject)
            || isInvalidOperationException(errorObject)
            || isInvalidApiOperationException(errorObject)
            || isArgumentException(errorObject)
            || isInvalidPermissionsException(errorObject);
        }

        resolveAddConflicts(localLogs: IIdDictionary<DVIRLog>) {
            return new Promise(resolve => {
                let calls: TMultiCallParam[] = [];

                for (let logId in localLogs) {
                    let dvir = localLogs[logId];
                    if (!dvir.id) {
                        throw new Error("Attempting to sync a record without an ID");
                    }

                    if (dvir.modified && dvir.modified === "Add") {
                        calls.push(["Get", {
                            typeName: "DVIRLog",
                            search: {
                                id: dvir.id
                            }
                        }]);
                    }
                }

                if (calls.length > 0) {
                    this.api.multiCall(calls, (results: DVIRLog[][]) => {
                        results.forEach(resultList => resultList);
                        for (let resultArray of results) {
                            let foundDvir = resultArray[0];
                            let localDvir = foundDvir && localLogs[foundDvir.id];
                            if (foundDvir && localDvir && localDvir.modified && localDvir.modified === "Add") {
                                localDvir.modified = "Set";
                            }
                        }
                        resolve();
                    }, error => {
                        handleApi(error);
                        resolve();
                    });
                } else {
                    resolve();
                }
            });
        }

        removeErroneousDvirs(localLogs: IIdDictionary<DVIRLog>, invalidLog: DVIRLog) {
            // If encountering the 'previous dvirlog is not certified' error
            // Remove any dvirs with the same resource after the erroring one because they will not be able to be saved
            let invalidLogAsset = invalidLog.isActive() ? invalidLog.getResource() : null;
            if (!invalidLog.isCertified() && invalidLogAsset) {
                let invalidLogAssetId = invalidLogAsset.id;
                let dvirDate = invalidLog.dateTime as string;
                let invalidLogs = hashToArray(localLogs)
                    .filter(dvir => new DVIRLog(dvir).getResource().id === invalidLogAssetId && dvir.modified === "Add"
                        && dvir.dateTime && dvir.dateTime > dvirDate);
                invalidLogs.forEach(dvir => {
                    delete localLogs[dvir.id];
                });
            }
        }

        clearVehicleIfNecessary(log: DVIRLog) {
            let currentVehicle = getCurrentVehicle();
            let logResourceType = log.getResourceType();
            let logVehicle = logResourceType === "device" && log[logResourceType];
            if (currentVehicle && logVehicle && logVehicle.id === logVehicle.id) {
                dispatcher.trigger(DispatcherEvents.VehicleRemoved);
            }
        }

        getNewRemark(id: string, dateTime: string, user: IIdEntity | null) {
            return { remark: "See Image",
                    mediaFiles: [{ id: id }],
                    dateTime: dateTime,
                    user: user ? { id: user.id } : undefined };
        }

        updateRepairRemarkWithMediaFile(defect: IDvirDefects, images: IImages, dvirLogDateTime: string, logModified: string) {
            let defectId = logModified === "Add" ? defect.defect.id : defect.id;
            let user: IUser | null = this.api.getUser();
            Object.keys(images).forEach((key) => {
                let image = images[key];
                if (!image.isLinkedWithRemark && !image.modified && image.isUploadedToCloud) {
                    if (image.defectId === defectId) {
                        if (!defect.defectRemarks || !defect.defectRemarks.length) {
                            defect.defectRemarks = [];
                            defect.defectRemarks.push(this.getNewRemark(key, dvirLogDateTime, user));
                        } else {
                            if (logModified === "Add") {
                                defect.defectRemarks[0].mediaFiles = [{ id: key }];
                            } else {
                                let existingRemarks: IDefectRemark[] = defect.defectRemarks;
                                let addedMedia = false;
                                for (let i = 0; i < existingRemarks.length; i++) {
                                    let currentRemark: IDefectRemark = existingRemarks[i];
                                    if (!currentRemark.mediaFiles) {
                                        currentRemark.mediaFiles = [{ id: key }];
                                        addedMedia = true;
                                        break;
                                    }
                                }
                                if (!addedMedia) {
                                    defect.defectRemarks.push(this.getNewRemark(key, dvirLogDateTime, user));
                                }
                            }
                        }
                        image.isLinkedWithRemark = true;
                    }
                }
            });
        }

        showDefectListToastrMessage (log: IDVIRLog) {
            let apiUser: IUser | null = this.api.getUser();
            let defectLists = this.defectListCache.get(undefined, apiUser?.id) || [];
            let deviceDefectLists = extend(true, {}, this.store.getState().deviceDefectLists[apiUser ? apiUser.id : ""] || {}) as IIdDictionary<IDefect[]>;
            let trailerDefectLists = extend(true, {}, this.store.getState().trailerDefectLists[apiUser ? apiUser.id : ""] || {}) as IIdDictionary<IDefect[]>;

            if (defectLists.length !== 0 && log.defectList) {
                const removedDefect = defectLists.find(dl => dl.id === log.defectList?.id);
                if (removedDefect) {
                    toastr.error(
                        `Defect list ${ removedDefect.name } was removed by administrator. Please pick another defect list to create the Asset Inspection`,
                        undefined,
                        undefined,
                        { isUserNightMode: this.store.getState().data.nightMode }
                    );
                }

                this.defectListCache.clear(apiUser?.id);
                this.defectListCache.add(defectLists.filter(d => d.id !== log.defectList?.id), apiUser?.id);

                let isDefectListUpdated = false;
                let isTrailerListUpdated = false;

                Object.keys(deviceDefectLists).map(function(deviceId: string) {
                        deviceDefectLists[deviceId].forEach(e => {
                            if (e.id === log.defectList?.id) {
                                let index = deviceDefectLists[deviceId].indexOf(e);
                                if (index !== -1) {
                                    deviceDefectLists[deviceId].splice(index, 1);
                                    isDefectListUpdated = true;
                                }
                            }
                        });
                    });
                if (isDefectListUpdated && apiUser) {
                    this.store.dispatch(DeviceDefectListActions.setDeviceDefectLists({
                        userId: apiUser.id,
                        defectLists: deviceDefectLists
                    }));
                }

                Object.keys(trailerDefectLists).map(function(trailerId: string) {
                    trailerDefectLists[trailerId].forEach(e => {
                        if (e.id === log.defectList?.id) {
                            let index = trailerDefectLists[trailerId].indexOf(e);
                            if (index !== -1) {
                                trailerDefectLists[trailerId].splice(index, 1);
                                isTrailerListUpdated = true;
                            }
                        }
                    });
                });

                if (isTrailerListUpdated && apiUser) {
                    this.store.dispatch(TrailerDefectListActions.setTrailerDefectLists({
                        userId: apiUser.id,
                        defectLists: trailerDefectLists
                    }));
                }
            }
        }

        /**
         * ESUPSD-53433: fills in repair info the server requires but the log was cached without.
         * The repair happened during the inspection, so the log's own dateTime is the truthful
         * timestamp - and unlike "now" it is in the past, so the server will not clamp it.
         * @returns whether any defect was updated, i.e. whether the push is worth retrying
         */
        backfillMissingRepairInfo (log: DVIRLog) {
            let defects: IDvirDefects[] | undefined = log.dVIRDefects;
            if (!defects || defects.length === 0) {
                return false;
            }

            // Both branches are narrowed to an id reference, matching what getUpdatedDefects sends
            // for repairUser - the server only needs the id, and passing the whole user through
            // would put fields on the defect that no other repair path sets.
            let repairUserSource = log.repairedBy !== "NoUserId" && log.repairedBy ? log.repairedBy : log.driver;
            let repairUser = repairUserSource !== "NoUserId" && repairUserSource
                ? { id: repairUserSource.id }
                : undefined;
            let repairDateTime = log.dateTime || getServerDate().toISOString();
            let backfilled = false;

            defects.forEach(defect => {
                // Only "Repaired" requires repair info. "NotNecessary" means no repair took
                // place, and the server rejects repair info on it, so leave it untouched.
                if (defect.repairStatus !== "Repaired") {
                    return;
                }
                if (!defect.repairDateTime) {
                    defect.repairDateTime = repairDateTime;
                    backfilled = true;
                }
                if (!defect.repairUser && repairUser) {
                    defect.repairUser = repairUser;
                    backfilled = true;
                }
            });

            return backfilled;
        }

        deleteDefectRepairUser (log: DVIRLog, defectId: string) {
            let defects: IDvirDefects[] | undefined = log.dVIRDefects;
            if (!defects || defects.length === 0) {
                return;
            }

            let removedUser: IIdEntity | undefined;
            for (let i = 0; i < defects.length; i++) {
                if (defects[i].id === defectId) {
                    removedUser = defects[i].repairUser;
                    break;
                }
            }

            if (removedUser) {
                if (log.repairedBy !== "NoUserId" && log.repairedBy.id === removedUser.id) {
                    log.repairedBy = "NoUserId";
                }

                defects.forEach(defect => {
                    if (defect.repairUser && defect.repairUser.id === removedUser?.id) {
                        delete defect.repairUser;
                    }

                    this.deleteRemarkUser(defect.defectRemarks, undefined, removedUser);
                });
            }
        }

        deleteRemarkUser (remarks?: IDefectRemark[], remarkId?: string, deletedUser?: IIdEntity) {
            if (!remarks || remarks.length === 0) {
                return;
            }

            let removedUser: IIdEntity | undefined = deletedUser;
            if (!removedUser && remarkId) {
                for (let i = 0; i < remarks.length; i++) {
                    if (remarks[i].id === remarkId) {
                        removedUser = remarks[i].user;
                        break;
                    }
                }
            }

            if (removedUser) {
                remarks.forEach(remark => {
                    if (remark.user && remark.user.id === (removedUser as IIdEntity).id) {
                        delete remark.user;
                    }
                });
            }
        }

        getTrailerAttachments(attachmentLookups: ITrailerAttachmentSearch[]): Promise<ITrailerAttachment[][]> {
            return new Promise((resolve, reject) => {
                if (attachmentLookups.length === 0) {
                    resolve([]);
                    return;
                }

                this.api.multiCall(
                    attachmentLookups.map(toTrailerAttachmentGetCall),
                    (results: unknown[][]) => resolve((results || []).map(normalizeTrailerAttachmentResults)),
                    reject
                );
            });
        }

        populateLocationWithLogRecords (localLogs: IIdDictionary<DVIRLog>, logsNeedingLocation: DVIRLog[]) {
            return new Promise((resolve, reject) => {
                let attachmentLookups: ITrailerAttachmentSearch[] = [];
                let trailerLookups: any[] = [];
                let logRecordLookups: common.ILogRecordLookup[] = [];

                logsNeedingLocation.forEach(log => {
                    if (this.isTrailerDvir(log)) {
                        attachmentLookups.push({
                            trailerSearch: {
                                id: log.trailer.id
                            },
                            activeFrom: log.dateTime,
                            activeTo: log.dateTime
                        });
                        trailerLookups.push(log);
                    } else if (this.isDeviceDvir(log) && log.dateTime) {
                        logRecordLookups.push({
                            id: log.id,
                            dateTime: log.dateTime,
                            deviceId: log.device.id
                        });
                    }
                });

                return this.getTrailerAttachments(attachmentLookups).then((attachmentArray) => {
                    attachmentArray.forEach((attachments, i) => {
                        if (attachments.length > 0) {
                            attachments.sort((a, b) => a.activeFrom > b.activeFrom ? -1 : 1);

                            let attachment = attachments[0];
                            let dvir = trailerLookups[i];

                            logRecordLookups.push({
                                id: dvir.id,
                                dateTime: dvir.dateTime,
                                deviceId: attachment.device.id
                            });
                        }
                    });

                    return common.getLogRecords(logRecordLookups, this.api);
                }).then((logRecords) => {
                    logRecords.forEach((record, i) => {
                        let location = {
                                location: {
                                    x: record.length > 0 ? record[0].longitude : 0,
                                    y: record.length > 0 ? record[0].latitude : 0
                                }
                            };
                        let logId = logRecordLookups[i].id;

                        let localLog = localLogs[logId];
                        if (localLog) {
                            localLog._tempLocation = location;
                        }
                    });

                    resolve(localLogs);
                }).catch(reject);
            });
        }

        populateMissingLocations(localLogs: IIdDictionary<DVIRLog>) {
            return new Promise(resolve => {
                let logsNeedingAddress: DVIRLog[] = [];

                hashToArray(localLogs).forEach((log: any) => {
                    // Populate server logs that do not have location
                    if (!log.location && !log._tempLocation && !(log._address && log._isManualAddress)) {
                        logsNeedingAddress.push(log);
                    }
                });

                // Get addresses for logs
                if (logsNeedingAddress.length > 0) {
                    this.populateLocationWithLogRecords(localLogs, logsNeedingAddress)
                        .then(
                            resolve,
                            errorObject => {
                                handleApi(errorObject, true);
                                resolve(localLogs);
                            }
                        );
                } else {
                    resolve(localLogs);
                }
            });
        }

        populateOdometers(logs: DVIRLog[]): Promise<DVIRLog[]> {
            return new Promise((resolve, reject) => {
                if (logs && logs.length) {
                    let currentUser: IUser | null = this.api.getUser();
                    let lookups: { index: number; call: TMultiCallParam }[] = [];

                    logs.forEach((log, i) => {
                        if (!log["_odometer"] && !log["odometer"]) {
                            lookups.push({
                                index: i,
                                call: [
                                    "CalculateHosOdometer",
                                    {
                                        deviceSearch: { id: log.device.id },
                                        dateTimes: [log.dateTime]
                                    }
                                ]
                            });
                        }
                    });

                    if (lookups.length > 0) {
                        logger.log(logger.Source.SYNC, `Populating _odometer for ${ lookups.length } DVIR logs`, currentUser);
                        this.api.multiCall(
                            lookups.map(lookUp => lookUp.call),
                                results => {
                                    results.forEach((res, i) => {
                                        let index = lookups[i].index;
                                        res[0] && (logs[index]["_odometer"] = res[0].valueInMeters);
                                    });
                                    resolve(logs);
                                },
                            reject
                        );
                    } else {
                        resolve(logs);
                    }
                } else {
                    resolve(logs);
                }
            });
        }

        populateEngineHours(logs: DVIRLog[]): Promise<DVIRLog[]> {
            return new Promise((resolve, reject) => {
                if (logs && logs.length) {
                    let currentUser: IUser | null = this.api.getUser();
                    let lookups: { index: number; call: TMultiCallParam }[] = [];

                    logs.forEach((log, i) => {
                        if (!log["_engineHours"] && !log["engineHours"]) {
                            lookups.push({
                                index: i,
                                call: [
                                    "Get",
                                    {
                                        typeName: "StatusData",
                                        search: {
                                            deviceSearch: { id: log.device.id },
                                            diagnosticSearch: { id: "DiagnosticEngineHoursAdjustmentId" },
                                            fromDate: log.dateTime,
                                            toDate: log.dateTime
                                        },
                                        resultsLimit: 1,
                                        propertySelector: {
                                            fields: ["data"],
                                            isIncluded: true
                                        }
                                    }
                                ]
                            });
                        }
                    });

                    if (lookups.length > 0) {
                        logger.log(logger.Source.SYNC, `Populating _engineHours for ${ lookups.length } DVIR logs`, currentUser);
                        this.api.multiCall(
                            lookups.map(lookUp => lookUp.call),
                                results => {
                                    results.forEach((res, i) => {
                                        let index = lookups[i].index;
                                        res[0] && (logs[index]["_engineHours"] = res[0].data);
                                    });
                                resolve(logs);
                            },
                            reject
                        );
                    } else {
                        resolve(logs);
                    }
                } else {
                    resolve(logs);
                }
            });
        }

        populateDefectSeverity(logs: any[], type: TDefectAssetType): Promise<any[]> {
            let traverseDefectsAddToSeverity = (defectList: IDefect[], defectSeverityLookup: {[id: string]: string}): void => {
                if (defectList && defectList.length) {
                    for (let i = 0; i < defectList.length; i++) {
                        let defect = defectList[i];
                        defectSeverityLookup[defect.id] = defect.severity;
                        traverseDefectsAddToSeverity(defect.children, defectSeverityLookup);
                    }
                }
            };

            return getDefectListsForAssetType(this.api, this.store, type).then(defectLists => {
                let defectSeverityLookup: {[id: string]: string} = {};
                defectLists.forEach(defectGroup => {
                    traverseDefectsAddToSeverity(defectGroup.children || [], defectSeverityLookup);
                });
                return defectSeverityLookup;
            }).then(defectSeverityLookup => {
                if (logs && logs.length) {
                    logs.forEach(log => {
                        if (log.defects && log.defects.length) {
                            log.defects.forEach(function (defect) {
                                if (defectSeverityLookup.hasOwnProperty(defect.id)) {
                                    defect.severity = defectSeverityLookup[defect.id];
                                }
                            });
                        }

                        if (log.dVIRDefects && log.dVIRDefects.length) {
                            log.dVIRDefects.forEach(defect => {
                                if (defectSeverityLookup.hasOwnProperty(defect.defect.id)) {
                                    defect.defect.severity = defectSeverityLookup[defect.defect.id];
                                }
                            });
                        }
                    });
                }
                return logs;
            });
        }

        /**
         * Convert logs retrieved from server (any type) to DVIRLog object by populating the default values in the constructor
         * @param dvirLogs Logs received from server
         * @returns Array of DVIRLog objects converted from any type objects
         */
        /**
         * @param reclassify Optional batch reclassifier. Pass one when converting several responses in
         *                   the same sync so the trailer-device snapshot is taken once, not per response.
         */
        convertDvirLogsFromServer(dvirLogs: IDVIRLog[], expectedTrailerId?: string, reclassify?: TTrailerDvirReclassifier): DVIRLog[] {
            const reclassifyLog = reclassify ?? this.makeTrailerDvirReclassifier();
            return dvirLogs.map(log => new DVIRLog(reclassifyLog(log, expectedTrailerId)));
        }

        getDvirsByLogTypes(newDvirs: DVIRLog[], includeBoundaryLogs: boolean, logTypes?: TLogType[], resultsLimit?: number): Promise<DVIRLog[]> {
            return new Promise((resolve, reject) => {
                if (newDvirs.length > 0) {
                    let calls: [string, IDvirSearchParams][] = [];
                    newDvirs.forEach(dvir => {
                        let dvirCall: [string, IDvirSearchParams] = ["Get", {
                            typeName: "DVIRLog",
                            search: {
                                fromDate: dvir.dateTime,
                                includeBoundaryLogs
                            }
                        }];

                        if (logTypes) {
                            dvirCall[1].search.logTypes = logTypes;
                        }

                        if (resultsLimit) {
                            dvirCall[1].resultsLimit = resultsLimit;
                        }

                        extend(dvirCall[1].search, toDvirAssetSearch(dvir));
                        calls.push(dvirCall);
                    });

                    this.api.multiCall(calls, (assetDvirs) => {
                        let serverLogs: DVIRLog[] = [];
                        if (assetDvirs && assetDvirs.length) {
                            assetDvirs.forEach(dvirsList => {
                                serverLogs.push.apply(serverLogs, this.convertDvirLogsFromServer(dvirsList));
                            });
                        }
                        resolve(serverLogs);
                    }, reject);
                } else {
                    resolve([]);
                }
            });
        }

        getAdditionalDvirs(newDvirs: DVIRLog[], firstDvirs: DVIRLog[], currentUser: IUser | null): Promise<DVIRLog[]> {
            let inTripLogs = firstDvirs.filter(dvir => dvir.logType === "InTrip");
            if (!inTripLogs.length) {
                return Promise.resolve(newDvirs);
            }

            let serverLogsDictionary: IIdDictionary<DVIRLog> = {};
            newDvirs.forEach(dvir => {
                serverLogsDictionary[dvir.id] = dvir;
            });

            return this.getDvirsByLogTypes(inTripLogs, true, ["PreTrip", "PostTrip"], 1)
                .then(serverLogs => {
                    serverLogs.forEach(serverDvir => {
                        serverLogsDictionary[serverDvir.id] = serverDvir;
                    });
                    return this.getDvirsByLogTypes(serverLogs, false);
                })
                .then(serverLogs => {
                    serverLogs.forEach(serverDvir => {
                        serverLogsDictionary[serverDvir.id] = serverDvir;
                    });
                    let combinedDvirList = hashToArray(serverLogsDictionary);
                    logger.log(logger.Source.SYNC, `Downloaded ${ combinedDvirList.length } DVIR records including additional logs`, currentUser);
                    return combinedDvirList;
                });
        }

        fetchTrailerLogs (localLogs: IIdDictionary<DVIRLog>): Promise {
            return new Promise((resolve, reject) => {
                let currentUser = this.api.getUser();
                let trailerAttachmentCollection = new TrailerAttachments(this.api, this.store);
                let calls: any[] = [];
                let feedRequests: any[] = [];
                let dvirTrailerIds = hashToArray(localLogs).filter(log => log.trailer?.id && log.trailer.id !== "NoTrailerId").map(log => (log.trailer as IIdEntity).id);

                let trailers = trailerAttachmentCollection.getCurrentTrailers();

                // Each iteration pushes exactly one call, so this is indexed in step with `calls`/`data`.
                let requestedTrailerIds: string[] = [];

                trailers.forEach((t, idx) => {
                    requestedTrailerIds[idx] = t.id;
                    let trailerVersion = this.trailerFeedVersion[t.id] || common.getFeedVersion(this.api, this.store, FeedTypeKey.DVIR_TRAILER, t.id);
                    if (dvirTrailerIds.indexOf(t.id) !== -1 && trailerVersion && trailerVersion !== MIN_VERSION) {
                        // Already has a DVIR log for the trailer
                        calls.push(["GetFeed", {
                            typeName: "DVIRLog",
                            fromVersion: trailerVersion,
                            search: {
                                ...toDvirAssetSearch({ trailer: t }),
                                includeRejected: true
                            }
                        }]);
                        feedRequests[idx] = {
                            id: t.id
                        };
                    } else {
                        let currentDate = getServerDate();
                        // Add three minutes grace period to the serverdate. It's possible a DVIRLog can exist with datetime greater than UTCNow
                        currentDate.setMinutes(currentDate.getMinutes() + 3);
                        calls.push(["Get", {
                            typeName: "DVIRLog",
                            search: {
                                ...toDvirAssetSearch({ trailer: t }),
                                fromDate: currentDate.toISOString(),
                                includeBoundaryLogs: true
                            },
                            resultsLimit: 1
                        }]);
                    }
                });

                if (calls.length > 0) {
                    this.api.multiCall(calls, (data) => {
                        let serverLogs: DVIRLog[] = [];
                        let firstDownloadLogs: DVIRLog[] = [];
                        // One snapshot for the whole batch — building it per response would take the
                        // cache deep-clone once per attached trailer, which is what this helper exists
                        // to avoid.
                        const reclassify = this.makeTrailerDvirReclassifier();

                        if (data && data.length) {
                            data.forEach((result, i) => {
                                // The request was scoped to a single trailer, so its id is authoritative for
                                // reclassifying the response — no need to fall back to the cache heuristic.
                                const requestedTrailerId = requestedTrailerIds[i];
                                if (result.hasOwnProperty("toVersion")) {
                                    const feedLogs = this.convertDvirLogsFromServer(result.data, requestedTrailerId, reclassify);
                                    serverLogs.push.apply(serverLogs, feedLogs);
                                    const trailerFeedInfo = feedRequests[i];
                                    this.trailerFeedVersion[trailerFeedInfo.id] = result.toVersion;
                                } else {
                                    let lastDvir = result[0];
                                    if (lastDvir) {
                                        let lastDvirLogObj = new DVIRLog(reclassify(lastDvir, requestedTrailerId));
                                        serverLogs.push(lastDvirLogObj);
                                        firstDownloadLogs.push(lastDvirLogObj);
                                        this.trailerFeedVersion[lastDvirLogObj.trailer.id] = lastDvir.version;
                                    }
                                }
                            });
                        }
                        logger.log(logger.Source.SYNC, `Downloaded ${ serverLogs.length } DVIR trailer records`, currentUser);
                        this.getAdditionalDvirs(serverLogs, firstDownloadLogs, currentUser)
                            .then(newDvirs => this.populateDefectSeverity(newDvirs, "Trailer"))
                            .then(resolve)
                            .catch(reject);
                    }, reject);
                } else {
                    resolve([]);
                }
            });
        }

        getNonCurrentVehicleIdsFromLogs(dvirVehicleLogs: DVIRLog[], currentVehicle: IIdEntity) {
            let visitedVehicleIds = {};
            let vehicleIds: string[] = [];
            for (let log of dvirVehicleLogs) {
                let logVehicleId = log.device.id;
                if (!(logVehicleId in visitedVehicleIds) && logVehicleId !== currentVehicle.id && log.modified) {
                    visitedVehicleIds[logVehicleId] = true;
                    vehicleIds.push(logVehicleId);
                }
            }

            return vehicleIds;
        }

        fetchVehicleLogs (localLogs: IIdDictionary<DVIRLog>): Promise {
            return new Promise((resolve, reject) => {
                let currentUser: IUser | null = this.api.getUser();
                let currentVehicle = getCurrentVehicle();

                if (!currentVehicle || currentVehicle.id === "NoDeviceId") {
                    resolve([]);
                    return;
                }

                let dvirVehicleLogs = (hashToArray(localLogs)).filter(log => this.isDeviceDvir(log));
                let dvirVersion = this.vehicleFeedVersion || common.getFeedVersion(this.api, this.store, FeedTypeKey.DVIR_VEHICLE);

                if (dvirVehicleLogs.length > 0 && dvirVersion && dvirVersion !== MIN_VERSION) {
                    if (currentVehicle) {
                        this.api.call("GetFeed", {
                            typeName: "DVIRLog",
                            fromVersion: dvirVersion,
                            search: {
                                deviceSearch: { id: currentVehicle.id },
                                includeRejected: true
                            }
                        }, feedResult => {
                            let serverLogs = this.convertDvirLogsFromServer(feedResult.data);
                            this.vehicleFeedVersion = feedResult.toVersion;
                            logger.log(logger.Source.SYNC, `Downloaded ${ serverLogs.length } DVIR vehicle records`, currentUser);
                            this.populateOdometers(serverLogs)
                                .then(populatedLogs => this.populateEngineHours(populatedLogs))
                                .then(populatedLogs => this.populateDefectSeverity(populatedLogs, "Device"))
                                .then(resolve)
                                .catch(reject);
                        }, reject);
                    } else {
                        resolve([]);
                    }
                } else {
                    let currentDate = getServerDate();
                    // Add three minutes grace period to the serverdate. It's possible a DVIRLog can exist with datetime greater than UTCNow
                    currentDate.setMinutes(currentDate.getMinutes() + 3);
                    this.api.call("Get", {
                        typeName: "DVIRLog",
                        search: {
                            deviceSearch: { id: currentVehicle.id },
                            fromDate: currentDate.toISOString(),
                            includeBoundaryLogs: true
                        },
                        resultsLimit: 1
                    }, data => {
                        logger.log(logger.Source.SYNC, `Downloaded ${ data.length } DVIR vehicle records`, currentUser);

                        if (data.length > 0) {
                            // only one log should get downloaded here
                            this.vehicleFeedVersion = data[0].version;
                        }

                        let dvirLogsData: DVIRLog[] = this.convertDvirLogsFromServer(data);
                        this.getAdditionalDvirs(dvirLogsData, dvirLogsData, currentUser)
                            .then(newDvirs => this.populateOdometers(newDvirs))
                            .then(populatedLogs => this.populateEngineHours(populatedLogs))
                            .then(populatedLogs => this.populateDefectSeverity(populatedLogs, "Device"))
                            .then(resolve)
                            .catch(reject);
                    }, reject);
                }
            });
        }

        fetchLogs(localLogs: IIdDictionary<DVIRLog>) {
            return syncDefectLists(this.securityService, this.api, this.store)
                .then(() => Promise.all([this.fetchVehicleLogs(localLogs), this.fetchTrailerLogs(localLogs)]))
                .then(([vehicleLogsToSync, trailerLogsToSync]) => this.mergeDvirLogs(vehicleLogsToSync.concat(trailerLogsToSync), localLogs));
        }

        populateMissingStatusData(localLogs: IIdDictionary<DVIRLog>) {
            return new Promise((resolve, reject) => {
                let logsNeedingOdometerData: DVIRLog[] = [];
                let logsNeedingEngineHoursData: DVIRLog[] = [];
                hashToArray(localLogs).forEach((log: DVIRLog) => {
                    const isLogModified = log.modified === "Add" || log.modified === "Set";
                    if (this.isDeviceDvir(log) && isLogModified) {
                        log.odometer = log.odometer || log._odometer;
                        if (!log.odometer) {
                            logsNeedingOdometerData.push(log);
                        }
                        log.engineHours = log.engineHours || log._engineHours;
                        if (!log.engineHours) {
                            logsNeedingEngineHoursData.push(log);
                        }
                    }
                });
                if (logsNeedingOdometerData.length > 0 || logsNeedingEngineHoursData.length > 0) {
                    Promise.all([this.populateOdometers(logsNeedingOdometerData), this.populateEngineHours(logsNeedingEngineHoursData)])
                        .then(() => {
                            logsNeedingOdometerData.forEach(log => {
                                log.odometer = log._odometer;
                            });
                            logsNeedingEngineHoursData.forEach(log => {
                                log.engineHours = log._engineHours;
                            });
                            resolve(localLogs);
                        }, reject);
                } else {
                    resolve(localLogs);
                }
            });
        }

        /**
         * MYG-157137: an unsynced trailer DVIR keeps the trailer id it was created with, which may belong
         * to the identity of the other side of DRIVE.ENABLE_TRAILER_DEVICE_LINK_API.RELEASE. Sent as is,
         * the server rejects it (fk_dvirlog_trailer, or "not in database") and the log is deleted.
         */
        reconcileTrailerIdentity(localLogs: IIdDictionary<DVIRLog>): Promise<IIdDictionary<DVIRLog>> {
            const unsyncedTrailerLogIds = Object.keys(localLogs).filter(id => localLogs[id].modified && isTrailerDvirRef(localLogs[id]));
            if (!unsyncedTrailerLogIds.length) {
                return Promise.resolve(localLogs);
            }
            return this.trailerIdentityReconciler
                .reconcile(unsyncedTrailerLogIds.map(id => localLogs[id].trailer.id))
                .then(translations => {
                    unsyncedTrailerLogIds.forEach(id => {
                        const trailer = localLogs[id].trailer;
                        const translatedId = translations[trailer.id];
                        translatedId && (trailer.id = translatedId);
                    });
                    return localLogs;
                });
        }

        fetchData(postSyncCallback) {
            this.vehicleFeedVersion = undefined;
            this.trailerFeedVersion = {};

            let localLogs: IIdDictionary<DVIRLog> = this.getLogs() || {};

            // A log may be pushed several times within one cycle, because each error branch below
            // recurses back into pushChangedToServer. Charging the retry budget once per cycle is
            // what turns "five attempts" into five chances over time rather than five in a row.
            this.syncCounterDecrementedThisCycle.clear();
            if (!this.firstSyncComplete) {
                this.restoreSyncRetryBudget(localLogs);
            }

            (this.firstSyncComplete ? Promise.resolve() : this.removeDeletedLogsFromServer(localLogs))
            .then(() => this.fetchLogs(localLogs))
            .then(logs => this.populateMissingStatusData(logs))
            .then(logs => this.reconcileTrailerIdentity(logs))
            .then(logs => this.pushChangedToServer(logs))
            .then(logs => this.getLogLocations(logs))
            .then(logs => this.populateMissingLocations(logs))
            .then(logs => this.populateTrailerDevice(logs))
            .then(logs => {
                let { prunedLogs: prunedLocalLogs, assetRelevantLogIds } = this.prune(logs);
                let apiUser: IUser | null = this.api.getUser();
                let hasCritical = (allLogs: IIdDictionary<DVIRLog>) => {
                    return hashToArray<DVIRLog>(allLogs).some(log => {
                        let dvirLog = new DVIRLog(log);
                        return dvirLog.hasCriticalUnrepairedDefects();
                    });
                };

                // Scoped to the current asset selection. A retained log for an asset the driver no
                // longer has selected must not raise this notification: dvir/main lists by current
                // asset, so there would be nothing for them to act on, and a log whose retries are
                // exhausted never leaves storage - the notification would be permanent.
                const assetRelevantLogs = assetRelevantLogIds.reduce((relevant, logId) => {
                    relevant[logId] = prunedLocalLogs[logId];
                    return relevant;
                }, {} as IIdDictionary<DVIRLog>);

                if (apiUser && apiUser.id) {
                    if (hasCritical(assetRelevantLogs)) {
                        Notifications.add("dvirCriticalDefects", {
                            type: "dvir",
                            message: "Major defects identified with vehicle and/or trailer(s).",
                            critical: true,
                            navigateTo: "dvir/main"
                        }, apiUser.id);
                    } else {
                        Notifications.remove("dvirCriticalDefects", apiUser.id);
                    }
                }

                // Not scoped to the current asset selection, unlike dvirCriticalDefects above. That
                // one is scoped because dvir/main lists by current asset and there would be nothing
                // to act on; this one is a statement that the fleet never received a record the
                // device holds the only copy of, which stays true whichever asset is selected.
                this.updateUnsyncedInspectionNotification(prunedLocalLogs, apiUser);

                this.setLogs(prunedLocalLogs);
                this.firstSyncComplete = true;
                this.vehicleFeedVersion && common.setFeedVersion(this.api, this.store, FeedTypeKey.DVIR_VEHICLE, this.vehicleFeedVersion, undefined);
                Object.keys(this.trailerFeedVersion).forEach(id => {
                    common.setFeedVersion(this.api, this.store, FeedTypeKey.DVIR_TRAILER, this.trailerFeedVersion[id], id);
                });

                dispatcher.trigger(DispatcherEvents.SyncDvir);
                postSyncCallback && postSyncCallback();
            }).catch((errorObject) => {
                postSyncCallback && postSyncCallback(errorObject);
                handleApi(errorObject);
            });
        }

        synchronize(profilingBatcher?: PerformanceProfileBatcher): Promise {
            return this.createProfiledSync(MeasureName.SyncDvir, () => {
                return new Promise((resolve) => {
                    this.fetchData((errorObject) => resolve(["DVIR", errorObject]));
                });
            }, profilingBatcher);
        }

        // Try to download the server version of the log and overwrite local copy

        private resetInvalidEdit(localLogs: IIdDictionary<DVIRLog>, invalidLog: IDVIRLog) {
            return new Promise((resolve, reject) => {

                // Get the server version of the bad log
                // If this fails then we won't really know what to do, so fail the sync with reject.
                this.api.call("Get", {
                    typeName: "DVIRLog",
                    search: {
                        id: invalidLog.id
                    }
                }, logs => {
                    if (logs.length > 0) {
                        // There should only be one. This is the 'good' copy of the log. Replace our local (bad) copy with this log and try pushing again.
                        delete localLogs[invalidLog.id];
                        logger.log(logger.Source.SYNC, "Updated local DVIR log from server", this.api.getUser());
                        // The local (bad) copy still knows whether this was a trailer inspection, which stays
                        // authoritative even if that trailer has since been detached and evicted from the cache.
                        const expectedTrailerId = isTrailerDvirRef(invalidLog) ? (invalidLog.trailer as IIdEntity).id : undefined;
                        localLogs[invalidLog.id] = new DVIRLog(this.reclassifyTrailerDvirWire(logs[0], expectedTrailerId));
                    } else if (invalidLog.modified === "Add") {
                        // ESUPSD-53433: there is no server copy to fall back to because the log was
                        // never synced. Deleting it here destroys an inspection the driver completed.
                        // Keep it - _syncCounter already bounds how many times it will be retried.
                        logger.error(
                            logger.Source.SYNC,
                            `Rejected DVIR log has no server copy, keeping the local record. DVIR ID: ${ invalidLog.id }.`
                        );
                    } else {
                        delete localLogs[invalidLog.id];
                    }
                    resolve();
                }, reject);
            });
        }

        /**
         * Keeps logs whose changes have not reached the server, even when they fall outside the
         * currently selected vehicle and trailers.
         *
         * prune() otherwise keeps only what is relevant to the current asset selection, so a
         * post-trip signed just before the driver unassigns their vehicle is discarded along with
         * the rest - and for an unsynced Add this device holds the only copy. pushChangedToServer
         * runs earlier in the same chain, so a log still marked modified here is one the push
         * deliberately skipped (retries exhausted, see MYG-143786) or could not deliver.
         *
         * Behind a release flag: this is the sync hot path, and retaining more than before trades
         * data loss for storage growth, so the previous behaviour has to stay reachable.
         *
         * Note that removeErroneousDvirs also discards logs carrying `modified`, and deliberately
         * so - it resolves duplicates created after an erroring log. That is a different intent and
         * is intentionally not covered by this guard (MYG-143888).
         */
        /**
         * True when the retry budget for `logId` has not been charged yet in this fetchData cycle.
         *
         * Each error branch in pushChangedToServer recurses immediately, so without this a log that
         * the server keeps rejecting burns all five attempts inside one cycle, with no interval in
         * which a transient server-side condition could clear. With the flag off the budget is
         * charged on every attempt, exactly as before.
         */
        private shouldChargeSyncRetry(log: IDVIRLog): boolean {
            // Add only. A rejected Set still has a server copy and is reconciled by
            // resetInvalidEdit; spreading its retries would delay that fallback, and this ticket
            // scopes Set out (MYG-143786).
            if (!this.featureFlagsService.hasFeatureFlag(FeatureFlagName.ENABLE_UNSYNCED_DVIR_LOG_TERMINAL_STATE)
                || log.modified !== "Add") {
                return true;
            }

            if (this.syncCounterDecrementedThisCycle.has(log.id)) {
                return false;
            }
            this.syncCounterDecrementedThisCycle.add(log.id);
            return true;
        }

        /**
         * Gives stranded logs their retry budget back once per session.
         *
         * A budget that reaches zero is otherwise permanent, so a log stays undeliverable even after
         * the condition that was rejecting it is fixed. Restoring on the first sync of a session
         * keeps the circuit breaker (five attempts per session) while letting a corrected server-side
         * problem drain the queue without anyone intervening.
         */
        private restoreSyncRetryBudget(localLogs: IIdDictionary<DVIRLog>) {
            if (!this.featureFlagsService.hasFeatureFlag(FeatureFlagName.ENABLE_UNSYNCED_DVIR_LOG_TERMINAL_STATE)) {
                return;
            }

            const restoredIds = Object.keys(localLogs).filter(logId => {
                const log = localLogs[logId];
                // Add only, for the same reason as shouldChargeSyncRetry: a Set has a server copy
                // to fall back on, and this ticket leaves its behaviour untouched.
                if (log.modified !== "Add" || log._syncCounter === undefined || log._syncCounter > 0) {
                    return false;
                }
                log._syncCounter = DVIR_SYNC_RETRY_BUDGET;
                return true;
            });

            if (restoredIds.length > 0) {
                logger.log(
                    logger.Source.SYNC,
                    `Restored the sync retry budget for DVIR logs stranded without a server copy. DVIR IDs: ${ restoredIds.join(", ") }.`,
                    this.api.getUser()
                );
            }
        }

        /**
         * Terminal state for an unsynced Add whose retries are spent: record enough to identify the
         * record later, and flag a runaway count. The driver-facing half is
         * updateUnsyncedInspectionNotification, which is state-driven so it can also clear.
         *
         * The record is never deleted here. The device holds the only copy, and deleting it on any
         * schedule is the defect this chain of tickets exists to remove (MYG-143728, MYG-143880,
         * MYG-143888). It is reported and kept instead. Since the server never received it, nothing
         * server-side can tell a fleet manager it exists - the driver is the only channel.
         *
         * Identifiers only, deliberately: the payload carries driver identity, remarks and image
         * references, and putting those in telemetry would need a privacy review (MYG-143786).
         */
        private reportTerminalUnsyncedLog(log: IDVIRLog, localLogs: IIdDictionary<DVIRLog>, currentUser: IUser | null) {
            if (!this.featureFlagsService.hasFeatureFlag(FeatureFlagName.ENABLE_UNSYNCED_DVIR_LOG_TERMINAL_STATE)
                || this.terminalUnsyncedLogIdsReported.has(log.id)) {
                return;
            }
            this.terminalUnsyncedLogIdsReported.add(log.id);

            const assetId = log.device?.id || log.trailer?.id || "unknown";
            logger.error(
                logger.Source.SYNC,
                `DVIR log reached its terminal undeliverable state and is retained on this device. DVIR ID: ${ log.id }. Asset: ${ assetId }. Signed: ${ log.dateTime || "unknown" }.`,
                currentUser
            );

            const terminalCount = Object.keys(localLogs).filter(logId => isTerminalUnsyncedAdd(localLogs[logId])).length;
            if (terminalCount > MAX_RETAINED_UNSYNCED_DVIR_LOGS) {
                // Detection only. Evicting here would delete the driver's only copy, so the cap
                // reports a runaway condition rather than resolving it; localStorage quota
                // exhaustion is already handled by the LocalStorageFull path.
                logger.error(
                    logger.Source.SYNC,
                    `Retained undeliverable DVIR logs exceeded the expected maximum. Count: ${ terminalCount }. Limit: ${ MAX_RETAINED_UNSYNCED_DVIR_LOGS }.`,
                    currentUser
                );
            }
        }

        /**
         * Drives the driver-facing notification from the current state of local storage, the same
         * way dvirCriticalDefects is driven, rather than only raising it at the moment retries run
         * out.
         *
         * The removal half is the point: restoreSyncRetryBudget hands a stranded log fresh attempts
         * each session, so a log the server was rejecting can still be delivered later once that
         * condition is corrected. Without a removal path the driver would keep being told their
         * inspection never reached the fleet after it has.
         */
        private updateUnsyncedInspectionNotification(localLogs: IIdDictionary<DVIRLog>, currentUser: IUser | null) {
            if (!this.featureFlagsService.hasFeatureFlag(FeatureFlagName.ENABLE_UNSYNCED_DVIR_LOG_TERMINAL_STATE)
                || !currentUser?.id) {
                return;
            }

            const userId = currentUser.id;
            if (!Object.keys(localLogs).some(logId => isTerminalUnsyncedAdd(localLogs[logId]))) {
                Notifications.remove("dvirUnsyncedInspection", userId);
                // A log that delivered can be stranded again later in this session, and that is a
                // new occurrence worth its own telemetry entry.
                this.terminalUnsyncedLogIdsReported.clear();
                return;
            }

            this.translator.translate(DVIR_UNSYNCED_INSPECTION_STRING)
                .then(translatedMessage => {
                    Notifications.add("dvirUnsyncedInspection", {
                        type: "dvir",
                        message: translatedMessage || DVIR_UNSYNCED_INSPECTION_STRING,
                        isTranslated: true,
                        navigateTo: "dvir/main"
                    }, userId);
                })
                .catch(error => handleApi(error));
        }

        private retainUnsyncedLogs(logCollection: DVIRLog[], prunedLogs: IIdDictionary<DVIRLog>) {
            const newlyRetainedIds: string[] = [];
            logCollection.forEach(log => {
                if (prunedLogs[log.id] || !isUnsyncedDvirLog(log)) {
                    return;
                }

                prunedLogs[log.id] = log.toJSON();
                if (!this.retainedUnsyncedLogIdsLogged.has(log.id)) {
                    this.retainedUnsyncedLogIdsLogged.add(log.id);
                    newlyRetainedIds.push(log.id);
                }
            });

            // Forget ids that are no longer present, so the set cannot grow for the life of the
            // instance and a log that syncs and later becomes stuck again is reported afresh.
            const presentIds = new Set(logCollection.map(log => log.id));
            this.retainedUnsyncedLogIdsLogged.forEach(id => {
                if (!presentIds.has(id)) {
                    this.retainedUnsyncedLogIdsLogged.delete(id);
                }
            });

            if (newlyRetainedIds.length > 0) {
                logger.log(
                    logger.Source.SYNC,
                    `DVIR prune kept logs not yet on the server that the current asset selection would have discarded. DVIR IDs: ${ newlyRetainedIds.join(", ") }.`,
                    this.api.getUser()
                );
            }
        }

        private hasUnsyncedCertifiedDvir(localLogs: IIdDictionary<DVIRLog>, invalidLog: DVIRLog) {
            let hasUpdatedLog = false;

            if (invalidLog.isActive()) {
                let errorLogResourceId = invalidLog.getResource().id;
                Object.keys(localLogs).forEach(logKey => {
                    let existingLog = new DVIRLog(localLogs[logKey]);
                    if (existingLog.getResource().id === errorLogResourceId && !existingLog.modified && existingLog.isCertified()) {
                        localLogs[logKey].modified = "Set";
                        hasUpdatedLog = true;
                    }
                });
            }

            return hasUpdatedLog;
        }

        private getLogLocations(localLogs: IIdDictionary<DVIRLog>) {
            return new Promise<IIdDictionary<DVIRLog>>((resolve, reject) => {
                let logsThatNeedLocation: DVIRLog[] = [];
                let logAddresses: string[] = [];

                // collect array of logs that need location coordinates & addresses (two separate arrays, as one will be used in the api call)
                Object.keys(localLogs).forEach(id => {
                    let log: DVIRLog = localLogs[id];

                    if (log._address && log._isManualAddress) {
                        logsThatNeedLocation.push(log);
                        logAddresses.push(log._address);
                    }
                });

                // no addresses to find
                if (logsThatNeedLocation.length === 0) {
                    resolve(localLogs);
                } else {
                    this.api.call("GetCoordinates", { "addresses": logAddresses }, addressArray => {
                        for (let i = 0; i < addressArray.length; i++) {
                            let log: DVIRLog = localLogs[logsThatNeedLocation[i].id];

                            if (addressArray[i] !== null) {
                                log.location = {
                                    location: {
                                        x: addressArray[i].x,
                                        y: addressArray[i].y
                                    }
                                };

                                log.modified = "Set";
                            }

                            delete log._address;
                            delete log._isManualAddress;
                        }

                        this.pushChangedToServer(localLogs)
                            .then(logs => {
                                resolve(logs);
                            })
                            .catch((err) => {
                                logger.log(logger.Source.SYNC, `Error in pushChangedToServer() ${ err.message }`);
                                reject(err);
                            });
                    }, reject);
                }
            });
        }

        /**
         * MYG-148762: under DRIVE.ENABLE_TRAILER_DEVICE_LINK_API.RELEASE the server keys a trailer DVIR on
         * the trailer's Device. A log whose device is a cached trailer device (or a currently attached
         * trailer) is therefore a trailer DVIR locally and is re-keyed onto `trailer`.
         *
         * The id set is snapshotted once per batch: it is read from the cache slot (a deep clone per
         * read) plus the current attachments, which is far too expensive to repeat for every log in a
         * feed of a few hundred.
         */
        private makeTrailerDvirReclassifier(): TTrailerDvirReclassifier {
            if (!usesTrailerDeviceIdentity(this.store)) {
                return log => log;
            }
            const trailerDeviceIds = this.getTrailerDeviceIds();
            return (log, expectedTrailerId) => fromTrailerDvirLogWire(log, deviceId => trailerDeviceIds.has(deviceId), expectedTrailerId);
        }

        /** One-shot snapshot of every device id that is known to be a trailer. */
        private getTrailerDeviceIds(): Set<string> {
            const cachedTrailerDevices = makeEffectiveTrailerDeviceCache(this.store).getDict(this.api.getUser()?.id);
            const trailerDeviceIds = new Set<string>(Object.keys(cachedTrailerDevices));
            new TrailerAttachments(this.api, this.store).getCurrentTrailers().forEach(trailer => trailerDeviceIds.add(trailer.id));
            return trailerDeviceIds;
        }

        /**
         * Single-log reclassification for a by-id re-fetch. `expectedTrailerId` comes from the local copy
         * of the log, which stays authoritative when the trailer has since been detached and evicted from
         * the cache — the cache heuristic alone would silently downgrade it to a vehicle inspection.
         */
        private reclassifyTrailerDvirWire(log: IDVIRLog, expectedTrailerId?: string): IDVIRLog {
            return this.makeTrailerDvirReclassifier()(log, expectedTrailerId);
        }

        private populateTrailerDevice(localLogs: IIdDictionary<DVIRLog>) {
            let localLogsArray = hashToArray(localLogs);
            let trailerDvirs = hashToArray(localLogs).filter(dvir => !!dvir.trailer && dvir.trailer.id && dvir.trailer.id !== "NoTrailerId" && !dvir._device && dvir.dateTime);
            let trailerAttachmentLookups: ITrailerAttachmentSearch[] = trailerDvirs.map(dvir => {
                    return {
                        trailerSearch: { id: dvir.trailer.id },
                        activeFrom: dvir.dateTime,
                        activeTo: dvir.dateTime
                    };
                });

            // For each trailer DVIR, look up the associated device (necessary for AUS/CAN ELD)
            return this.getTrailerAttachments(trailerAttachmentLookups)
                .then(attachments => {
                    attachments.forEach((trailerAttachment, index) => {
                        trailerAttachment.sort((a, b) => a.activeFrom > b.activeFrom ? -1 : 1);

                        let foundAttachment = trailerAttachment[0];
                        if (foundAttachment && foundAttachment.device) {
                            trailerDvirs[index]._device = foundAttachment.device;
                        }
                    });

                    let deviceLookupDictionary: IIdDictionary<IIdEntity> = {};
                    localLogsArray.forEach(dvir => {
                        if (dvir._device) {
                            deviceLookupDictionary[dvir._device.id] = dvir._device;
                        }
                    });
                    return this.deviceCache.getOrFetch(this.api, hashToArray(deviceLookupDictionary), this.api.getUser()?.id);
                })
                .then(() => localLogs);
        }
    }